import { QueryClient } from "@tanstack/react-query";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";

// v1 web: localStorage. v2 native (Capacitor): swap for Capacitor Preferences adapter.
// v2 RN: swap for AsyncStorage adapter. Adapter must implement { getItem, setItem, removeItem }.
// This is the only place storage is referenced — swapping it later is a one-line change
// with no hook touches.
const storage: Storage = window.localStorage;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,            // 1 min — fresh enough for an offline-first PWA
      gcTime: 24 * 60 * 60 * 1000,  // 24 hr — keep in cache even if unmounted
      refetchOnWindowFocus: false,  // PWA, not a dashboard
      refetchOnReconnect: true,     // useful when network drops
      retry: 1,                     // one retry on transient failure
    },
  },
});

export const persister = createSyncStoragePersister({
  storage,
  key: "mori-query-cache-v1",
});

// Queries whose data contains Map/Set (or other non-JSON-safe values) must NOT
// be persisted — JSON.stringify silently turns Maps/Sets into `{}`, and the
// rehydrated shape crashes consumers that call .get/.has on it.
// See useFeedEngineData.ts → fetchCollectionStepIndex (returns Map + Sets).
const NON_PERSISTABLE_QUERY_KEYS = new Set<string>([
  "collection-step-index",
]);

// 24h max age + dehydrate only successful queries — feed straight into
// PersistQueryClientProvider's `persistOptions` prop in App.tsx.
// `buster` is the Vercel commit SHA injected at build time (vite.config.ts):
// every deploy → new SHA → all persisted queries invalidate on next load,
// so a query whose shape changed can't crash returning users from stale cache.
export const persistOptions = {
  persister,
  maxAge: 24 * 60 * 60 * 1000,
  buster: __APP_VERSION__,
  dehydrateOptions: {
    shouldDehydrateQuery: (query: {
      state: { status: string };
      queryKey: readonly unknown[];
    }) => {
      if (query.state.status !== "success") return false;
      const root = query.queryKey[0];
      if (typeof root === "string" && NON_PERSISTABLE_QUERY_KEYS.has(root)) {
        return false;
      }
      return true;
    },
  },
};
